import React, {useState, useEffect} from 'react'
import GenericTable from '../GenericTable/table'
import {feedbackData, fetchFeedbackData} from './helper'
import SkeletonCard from '../Auth/Components/SkeletonCard'
import { Tooltip } from 'react-tooltip'


type SortConfig = {key: string; direction: 'asc' | 'desc' | null}
const FeedBack = () => {
  const [columns, setColumns] = useState<string[]>([])
  const [data, setData] = useState<Record<string, any>[]>([])
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [sortConfig, setSortConfig] = useState<SortConfig>({key: '', direction: null})
  const [currentPage, setCurrentPage] = useState(1)
  const [currentPageData, setCurrentPageData] = useState<Record<string, any>[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [totalPages, setTotalPages] = useState<number>(1)
  const itemsPerPage = 10

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await fetchFeedbackData(currentPage, itemsPerPage);
  
        const rawColumns = feedbackData.columns;
        const rawData = response.feedbacks; 
  
        setColumns(rawColumns);
        setData(mapDataToColumns(rawData, rawColumns));
      } catch (error) {
        console.error("Error fetching feedback data:", error);
      } finally {
        setLoading(false);
      }
    };
  
    fetchData();
  }, []);
  

  const mapDataToColumns = (
    data: Record<string, any>[],
    columns: string[]
  ): Record<string, any>[] => {
    if (!data || !Array.isArray(data)) {
      console.warn('mapDataToColumns received invalid data:', data)
      return []
    }

    return data.map((row) => {
      const mappedRow: Record<string, any> = {}
      columns.forEach((col) => {
        const key = col.replace(/\s+/g, '')
        mappedRow[col] = row[key] || ''
      })
      return mappedRow
    })
  }

  const sortData = (data: Record<string, any>[], column: string) => {
    const sortableData = [...data]
    sortableData.sort((a, b) => {
      const aValue = a[column]
      const bValue = b[column]

      const aDate = Date.parse(aValue)
      const bDate = Date.parse(bValue)
      if (!isNaN(aDate) && !isNaN(bDate)) {
        return sortConfig.direction === 'asc' ? aDate - bDate : bDate - aDate
      }

      if (!isNaN(aValue) && !isNaN(bValue)) {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortConfig.direction === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue)
      }

      return 0
    })

    return sortableData
  }

  const handleSort = (column: string) => {
    setSortConfig((prevConfig) => {
      const direction = prevConfig.key === column && prevConfig.direction === 'asc' ? 'desc' : 'asc'
      return {key: column, direction}
    })

    const sortedData = sortData(data, column)
    setData(sortedData)
    //setCurrentPage(1); // Reset to first page after sorting
  }

  const handleNextClick = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prevPage) => prevPage + 1)
    }
  }

  const handlePreviousClick = () => {
    if (currentPage > 1) {
      setCurrentPage((prevPage) => prevPage - 1)
    }
  }

  
  useEffect(() => {
    const filteredData = data.filter((row) => {
      const matchesSearch = Object.values(row)
        .join(' ')
        .toLowerCase()
        .includes(searchTerm.toLowerCase())

      return matchesSearch
    })
    setTotalPages(Math.ceil(filteredData.length / itemsPerPage))

    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    const currentPageData = filteredData.slice(startIndex, endIndex)

    setCurrentPageData(currentPageData)
  }, [data, currentPage, searchTerm])

  return (
    <div>
      {loading ? (
        <SkeletonCard />
      ) : (
        <>
          <GenericTable
            columns={columns}
            data={currentPageData}
            handleSort={handleSort}
            sortConfig={sortConfig}
            searchTerm={searchTerm}
            onSearch={(term) => {
              setSearchTerm(term)
              setCurrentPage(1)
            }}


            renderCell={(column, row, rowIndex) => {
              if(column === 'Message') {
                const text = row[column] || "";
                const maxLength = 20;
                const truncatedText = text.length > maxLength ? text.slice(0, maxLength) + "..." : text;
              
                return (
                  <>
                    <span data-tooltip-id="comment-tooltip" data-tooltip-content={text}>
                      {truncatedText}
                    </span>
                    <Tooltip id="comment-tooltip" 
                        place="bottom" // Tooltip position: top, bottom, left, right
                        style={{ backgroundColor: "#6D9300", color: "#fff", padding: "4px", borderRadius: "6px" }}/>
                  </>
                );
              }
              return row[column]
            }}
          />
          

          <ul className='pagination justify-content-center' style={{margin: '15px 0'}}>
            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
              <button
                className='page-link'
                onClick={() => {
                  if (handlePreviousClick) handlePreviousClick()
                }}
                disabled={currentPage === 1}
                aria-label='Previous'
              >
                &laquo; Previous
              </button>
            </li>

            <li className='page-item active'>
              <div className='d-flex align-items-center'>
                <button
                  className='page-link'
                  style={{backgroundColor: '#6D9300', color: '#fff', pointerEvents: 'none'}}
                >
                  {currentPage}
                </button>
                <span className='mx-2'>of</span>
                <button
                  className='page-link'
                  style={{backgroundColor: '#6D9300', color: '#fff', pointerEvents: 'none'}}
                >
                  {totalPages}
                </button>
              </div>
            </li>

            <li className={`page-item ${currentPage >= totalPages ? 'disabled' : ''}`}>
              <button
                className='page-link'
                onClick={() => {
                  if (handleNextClick) handleNextClick()
                }}
                disabled={currentPage >= totalPages}
                aria-label='Next'
              >
                Next &raquo;
              </button>
            </li>
          </ul>
        </>
      )}
    </div>
  )
}

export default FeedBack
